// ------------------------------------------------------------------
// PURE MODULE: scroll_momentum_boilerplate.mod.js
// ------------------------------------------------------------------
import gsap from "gsap";

export function ScrollMomentumBoilerplate() {
    let instanceConfig = {};
    let proxy = { value: 0 };
    let elementUnits = [];

    return {
        init(customConfig = {}) {
            instanceConfig = { ...ScrollMomentumBoilerplate.defaults, ...customConfig };
            const wrappers = document.querySelectorAll(instanceConfig.targets);

            // =========================================================
            // [UNIVERSAL CORE]: LAZY-BUILD OBSERVER
            // =========================================================
            const buildObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        if (entry.target._buildAnimation) entry.target._buildAnimation(); 
                    } else {
                        // Execute the turn to master manager approach teardown
                        if (entry.target._destroyAnimation) entry.target._destroyAnimation();
                    }
                });
            }, { rootMargin: "800px 0px" });

            elementUnits = Array.from(wrappers).map((wrapper, index) => {
                
                // =========================================================
                // [REPLACEABLE SHELL]: DOM QUERIES & LOCAL STATE
                // Customize these variables for your specific effect
                // =========================================================
                const svg = wrapper.querySelector('svg'); 
                const img = wrapper.querySelector('img');
                const video = wrapper.querySelector('video');

                if (!svg) return () => {}; // Skip if no anchor exists

                let isBuilt = false;
                let isInView = false; 
                let isPinnedOverride = false;
                let localAnim; 
                let canvas; 
                const speed = instanceConfig.speeds[index] ?? instanceConfig.speeds[instanceConfig.speeds.length - 1];
                let isPlaying = false;
                let playPromise; 

                // =========================================================
                // [UNIVERSAL CORE]: STATE SETTERS
                // Triggered externally by Global IO and Main Loop
                // =========================================================
                wrapper._setVisibility = (visible) => { isInView = visible; };
                
                wrapper._setOverride = (pinned) => { 
                    isPinnedOverride = pinned; 
                    // [REPLACEABLE SHELL]: Graceful exit animation
                    if (pinned && localAnim && localAnim.progress() < 1) {
                        gsap.to(localAnim, { progress: 1, duration: 0.5, ease: "power2.out" });
                    }
                };
                
                // =========================================================
                // [REPLACEABLE SHELL]: GARBAGE COLLECTION
                // Define exactly what happens when the element is 800px away
                // =========================================================
                wrapper._destroyAnimation = () => {
                    if (!isBuilt) return;
                    
                    if (localAnim) {
                        localAnim.progress(1).kill(); 
                    }
                    
                    if (canvas && canvas.parentNode) {
                        canvas.parentNode.replaceChild(svg, canvas); 
                    }
                    
                    canvas = null; 
                    isBuilt = false; 
                };

                // =========================================================
                // [REPLACEABLE SHELL]: THE BUILD LOGIC
                // Define exactly what happens when the element approaches view
                // =========================================================
                wrapper._buildAnimation = () => {
                    if (isBuilt) return; 
                    
                    canvas = document.createElement('canvas');
                    canvas.style.cssText = "position:absolute; top:0; left:0; width:100%; height:100%; z-index:3; pointer-events:none;";
                    
                    canvas.width = Math.min(2000, window.innerWidth * (window.devicePixelRatio || 1));
                    canvas.height = 10;
                    const ctx = canvas.getContext('2d');

                    if (svg.parentNode) {
                        svg.parentNode.replaceChild(canvas, svg);
                    } else {
                        wrapper.appendChild(canvas);
                    }

                    const stripes = [];
                    for (let i = 0; i < 20; i++) {
                        stripes.push({ scaleX: 1.1, x: 0 }); 
                    }

                    const drawCanvas = () => {
                        if (!canvas) return;
                        ctx.clearRect(0, 0, canvas.width, canvas.height); 
                        ctx.fillStyle = "white";
                        const baseWidth = canvas.width / 20;

                        for (let i = 0; i < 20; i++) {
                            const s = stripes[i];
                            if (s.scaleX <= 0.001) continue; 
                            
                            const center_x = (i * baseWidth) + (baseWidth / 2) + (s.x * baseWidth);
                            const currentWidth = baseWidth * s.scaleX;
                            
                            ctx.fillRect(center_x - (currentWidth / 2), 0, currentWidth + 1, canvas.height);
                        }
                    };

                    localAnim = gsap.timeline({ paused: true, onUpdate: drawCanvas });
                    
                    localAnim.fromTo(stripes, 
                        { scaleX: 1.1, x: 0 },
                        { scaleX: 0, x: 2, duration: 1.2, ease: "power2.inOut", stagger: { each: 0.05, from: "edges" } }
                    );

                    localAnim.progress(1);

                    if (video) {
                        gsap.set(img, { opacity: 0 });
                        gsap.set(video, { opacity: 1 });
                    }

                    isBuilt = true; 
                };

                buildObserver.observe(wrapper);

                // =========================================================
                // [UNIVERSAL CORE / REPLACEABLE SHELL]: THE RENDER LOOP
                // =========================================================
                return (gap) => {
                    // --- Core Safety Gates ---
                    if (!isBuilt) return; 
                    if (!isInView) return; 
                    if (isPinnedOverride) return; 
                    
                    // --- Replaceable Shell Logic ---
                    const absDrift = Math.abs(gap * speed * instanceConfig.intensity);
                    let animProgress = gsap.utils.clamp(0, 1, 1 - (absDrift / instanceConfig.wipeSensitivity));
                    if (animProgress > 0.99) animProgress = 1; 

                    // The Dirty Flag optimization (Do not update if unchanged)
                    if (localAnim.progress() === animProgress) return;
                    localAnim.progress(animProgress);

                    if (video) {
                        if (animProgress > 0 && !isPlaying) {
                            isPlaying = true;
                            if (video.readyState >= 2) { 
                                playPromise = video.play(); 
                                if (playPromise !== undefined) {
                                    playPromise.catch(() => { isPlaying = false; }); 
                                }
                            } else {
                                video.addEventListener('loadeddata', () => {
                                    if (isPlaying) video.play().catch(() => { isPlaying = false; });
                                }, { once: true });
                            }
                        } else if (animProgress === 0 && isPlaying) {
                            isPlaying = false;
                            if (playPromise !== undefined) {
                                playPromise.then(() => {
                                    video.pause();
                                    video.currentTime = 0;
                                }).catch(() => {});
                            } else {
                                video.pause();
                                video.currentTime = 0;
                            }
                        }
                    }
                };
            });

            if (window.App?.progress) proxy.value = window.App.progress.value;
        },

        // =========================================================
        // [UNIVERSAL CORE]: THE MASTER MOMENTUM DRIVER
        // Do not modify this block for new effects.
        // =========================================================
        update(currentScroll) { 
            if (elementUnits.length === 0) return;
            
            let diff = currentScroll - proxy.value;
            
            // 0.1px sleep state limits CPU usage
            if (Math.abs(diff) < 0.1) {
                proxy.value = currentScroll;
                diff = 0;
            } else {
                proxy.value += diff * instanceConfig.lerp;
            }
            
            if (diff === 0) return; 

            const gap = currentScroll - proxy.value; 

            for (let i = 0; i < elementUnits.length; i++) {
                elementUnits[i](gap);
            }
        }
    };
}

// Default Configuration
ScrollMomentumBoilerplate.defaults = {
    targets: ".lazy-reveal-wrapper",
    speeds: [100], 
    lerp: 0.05,
    intensity: 0.05,
    wipeSensitivity: 4000 
};